import * as React from 'react';
import { useState } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import MapIcon from '@mui/icons-material/Map';
import { useChemicalProductStyles } from '../ChemicalProduct.styles';
import { TApiChemicalProductHazard, TApiChemicalProductSignalWord } from '../../../types/Chemical';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { ImageList, useMediaQuery, useTheme } from '@mui/material';
import { useSelectedLocation } from '../../../hooks/useSelectedLocation';
import LocationSelectModal from '../LocationSelectModal';

interface IProtectiveMeasurements {
  exposureControls: string;
  recommendedUse: string;
  ppe: Array<TApiChemicalProductHazard>;
  hazards: Array<TApiChemicalProductHazard>;
  signalWordType?: TApiChemicalProductSignalWord;
}

const resolveInstructionText = (
  ppeItem: TApiChemicalProductHazard,
  selectedLocationId: number | null
): string => {
  const list = ppeItem.instructions;
  if (!list || list.length === 0) {
    return ppeItem.description || '';
  }
  if (selectedLocationId !== null) {
    const match = list.find(
      (i) => i.locationId === selectedLocationId && (i.text || '').trim() !== ''
    );
    if (match) {
      return match.text;
    }
  }
  const fallback = list.find(
    (i) => i.locationId === null && (i.text || '').trim() !== ''
  );
  return fallback ? fallback.text : '';
};

const productHasLocationSpecificInstruction = (
  ppe: Array<TApiChemicalProductHazard>
): boolean => {
  return ppe.some((p) =>
    (p.instructions || []).some(
      (i) => i.locationId !== null && (i.text || '').trim() !== ''
    )
  );
};

const ProtectiveMeasurements: React.FC<IProtectiveMeasurements> = ({
  exposureControls,
  recommendedUse,
  ppe,
  hazards,
  signalWordType,
}): JSX.Element => {
  const { t } = useTranslation();
  const classes = useChemicalProductStyles({});
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedLocationId, selectedLocation, setSelectedLocationId, userLocations } =
    useSelectedLocation();
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  const showLocationChip = productHasLocationSpecificInstruction(ppe);

  return (
    <div className={classes.protectiveMeasurementsContainer}>
      {showLocationChip && selectedLocation && (
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            icon={<MapIcon style={{ color: 'rgba(0, 0, 0, 0.87)' }} />}
            label={selectedLocation.title}
            onClick={() => setLocationModalOpen(true)}
            color="warning"
            variant="outlined"
            sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
          />
        </div>
      )}

      <LocationSelectModal
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        onApply={(id) => setSelectedLocationId(id)}
        locations={userLocations}
        currentLocationId={selectedLocationId}
      />

      <ImageList rowHeight={'auto'} cols={isXs ? 1 : 4} className={classes.iconList}>
        {ppe.map((e) => (
          <ListItem key={e.label} className={classes.ppeListItem}>
            <ListItemAvatar>
              <Avatar className={classes.hazardIcon}>
                <img src={e.image} alt={e.label} />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={e.label}
              secondary={resolveInstructionText(e, selectedLocationId)}
            />
          </ListItem>
        ))}
      </ImageList>

      {exposureControls && (
        <div
          className={classes.protectiveMeasurementsContainerText}
          dangerouslySetInnerHTML={{ __html: `<b>${t('chemicals.exposure_controls')}</b> <p>${exposureControls}</p>` }}
        />
      )}
      {recommendedUse && (
        <>
          <div style={{
            borderStyle: 'dashed',
            borderColor: '#F9C682',
            borderWidth: 1,
            borderRadius: 1,
          }}>
          </div>
          <div
            className={classes.protectiveMeasurementsContainerText}
            dangerouslySetInnerHTML={{ __html: `<b>${t('chemicals.recommended_use')}</b> <p>${recommendedUse}</p>` }}
          />
        </>
      )}

      {(hazards && hazards.length > 0) && (
        <>
          <div style={{
            borderStyle: 'dashed',
            borderColor: '#F9C682',
            borderWidth: 1,
            borderRadius: 1,
            marginBottom: '10px',
          }}>
          </div>
          <ImageList rowHeight={80} cols={isXs ? 1 : 4} className={classes.iconList}>
            {
              hazards.map((hazard) => {
                return (
                  <ListItem key={hazard.label}>
                    <ListItemAvatar>
                      <Avatar className={classes.hazardIcon}>
                        <img src={hazard.image} alt='hazard.label' />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={hazard.label} />
                  </ListItem>
                );
              })
            }
          </ImageList>
        </>
      )}

      {(signalWordType && signalWordType.name !== 'chemicals.signal_word_none') && (
        <Typography
          className={classes.protectiveMeasurementsContainerText}
          dangerouslySetInnerHTML={{ __html: `<b>${t('chemicals.signal_word')}</b> <p style="color: ${signalWordType.color}; display: inline; padding-left: 15px">${t(signalWordType.name)}</p>` }}
        />
      )}
    </div>
  );
};

export default ProtectiveMeasurements;
